import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { environment as localEnvironment } from '../../environments/environment';
import { Observable, Subject } from 'rxjs';
import { RouterUtilityService } from './router.utility.service';

@Injectable()

export class PdfService {
    baseUrl: string;
    env = localEnvironment;

    constructor(private http: HttpClient,
        private routerUtilityService: RouterUtilityService) {
        this.baseUrl = this.env.baseUrl.backend.main;
    }

    post(url: string, data, options?: {}, shouldSendAuthBearerToken: boolean = true): Observable<any> {
        data = this.prepareDataFormat(data);
        const formData: FormData = this.toFormData(data);

        return this.simpleXhrRequest(formData, url, shouldSendAuthBearerToken);
    }

    _post(url: string, data, options?: {}): Observable<any> {
        data = this.prepareDataFormat(data);
        return this.http.post(this.optimizeUrl(this.baseUrl + url), data, options);
    }

    postSpecial(url: string, data, options?: {}): Observable<any> {
        return this.http.post(this.optimizeUrl(this.baseUrl + url), data, options);
    }

    get(url: string, options?: {}): Observable<any> {
        return this.http.get(this.optimizeUrl(this.baseUrl + url), options);
    }

    delete(url: string, options?: {}): Observable<any> {
        return this.http.delete(this.optimizeUrl(this.baseUrl + url), options);
    }

    head(url: string, options?: {}): Observable<any> {
        return this.http.head(this.optimizeUrl(this.baseUrl + url), options);
    }

    put(url: string, data, options?: {}, shouldSendAuthBearerToken: boolean = true): Observable<any> {
        data = this.prepareDataFormat(data);
        const formData: FormData = this.toFormData(data);
        formData.append('_method', 'PUT');
        return this.simpleXhrRequest(formData, url, shouldSendAuthBearerToken);
    }

    _put(url: string, data, options?: {}): Observable<any> {
        data = this.prepareDataFormat(data);
        return this.http.put(this.optimizeUrl(this.baseUrl + url), data, options);
    }

    options(url: string, options?: {}): Observable<any> {
        return this.http.options(this.optimizeUrl(this.baseUrl + url), options);
    }

    patch(url: string, data, options?: {}): Observable<any> {
        data = this.prepareDataFormat(data);
        return this.http.patch(this.optimizeUrl(this.baseUrl + url), data, options);
    }

    jsonp(url: string, options = ''): Observable<any> {
        return this.http.jsonp(this.optimizeUrl(this.baseUrl + url), options);
    }

    download(url: string): Observable<any> {
        return this.http.get(this.optimizeUrl(this.baseUrl + url), { observe: 'response', responseType: 'blob' });
    }

    prepareDataFormat(data): FormData {

        if (data.constructor.name === 'Array') {
            this.iterateArray(data);
        } else if (data.constructor.name === 'Object') {
            this.iterateObject(data);
        }
        return data;
    }

    iterateArray(data) {
        for (let i = 0; i < data.length; i++) {
            const property = data[i];
            if (property instanceof Date) {
                data[i] = property.getFullYear() + '-' + (property.getMonth() + 1) + '-' + property.getDate() + ` ${property.getHours()}:${property.getMinutes()}:${property.getSeconds()}`;
            } else if (property && property.constructor.name === 'Array') {
                this.iterateArray(property);
            } else if (property && property.constructor.name === 'Object') {
                this.iterateObject(property);
            }
        }
    }

    iterateObject(data) {
        for (const property in data) {
            if (data.hasOwnProperty(property)) {
                if (data[property] instanceof Date) {
                    data[property] = data[property].getFullYear() + '-' + (data[property].getMonth() + 1) + '-' + data[property].getDate() + ` ${data[property].getHours()}:${data[property].getMinutes()}:${data[property].getSeconds()}`;
                } else if (data[property] && data[property].constructor.name === 'Array') {
                    this.iterateArray(data[property]);
                } else if (data[property] && data[property].constructor.name === 'Object') {
                    this.iterateObject(data[property]);
                }
            }
        }
    }

    toFormData(obj) {
        const fd = new FormData();
        for (const property in obj) {
            if (obj.hasOwnProperty(property)) {
                if (typeof obj[property] !== 'object' && !(obj[property] instanceof File)) {
                    fd.append(property, obj[property]);
                } else if (typeof obj[property] === 'object') {
                    if (obj[property] && 'file' in obj[property]) {
                        fd.append('__' + property, obj[property].file);
                    }
                    const temp = JSON.stringify(obj[property]);
                    fd.append(property, temp);
                }
            }
        }
        return fd;
    }

    simpleXhrRequest(data, url, shouldSendAuthBearerToken?): Observable<any> {
    const observer = new Subject<any>();
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${this.baseUrl}${this.optimizeUrl(url)}`);

    // Add authorization token if needed
    if (shouldSendAuthBearerToken) {
        xhr.setRequestHeader('Authorization', localStorage.getItem('session') || '');
    }

    // Set responseType to 'arraybuffer' to handle binary and text responses
    xhr.responseType = 'arraybuffer';

    xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
            const headers = new HttpHeaders(xhr.getAllResponseHeaders());
            const newSession = headers.get('authorization');
            if (newSession) {
                localStorage.setItem('session', newSession);
            }

            const contentType = headers.get('Content-Type') || '';

            if (xhr.status < 300) {
                // Handle PDF or other binary responses
                if (contentType.includes('application/pdf')) {
                    const blob = new Blob([xhr.response], { type: 'application/pdf' });
                    observer.next(blob);
                } else if (contentType.includes('application/json')) {
                    try {
                        const decodedText = new TextDecoder().decode(xhr.response);
                        const body = JSON.parse(decodedText);
                        observer.next(new HttpResponse({
                            body,
                            headers,
                            status: xhr.status,
                            statusText: xhr.statusText
                        }));
                    } catch (error) {
                        observer.error(new HttpErrorResponse({
                            error: 'Error parsing JSON',
                            headers,
                            status: xhr.status,
                            statusText: xhr.statusText
                        }));
                    }
                } else {
                    // fallback: return raw response
                    observer.next(xhr.response);
                }
            } else {
                try {
                    const decodedText = new TextDecoder().decode(xhr.response);
                    const errorBody = JSON.parse(decodedText);
                    observer.error(new HttpErrorResponse({
                        error: errorBody,
                        headers,
                        status: xhr.status,
                        statusText: xhr.statusText
                    }));
                } catch (e) {
                    observer.error(new HttpErrorResponse({
                        error: 'Unknown error or non-JSON response',
                        headers,
                        status: xhr.status,
                        statusText: xhr.statusText
                    }));
                }
            }
        }
    };

    xhr.send(data);
    return observer;
}


    optimizeUrl(url) {
        return url.charAt(url.length - 1) === '/' ? url.substring(0, url.length - 1) : url;
    }
}
